import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { defaultSelfServeGating } from 'src/app/constants/billing.constants';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData, DiscountApiData } from 'src/app/pages/new-billing/types/api.types';
import { AddonIds, AddonPricingService } from 'src/app/services/addon-pricing/addon-pricing.service';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { BillingApiService } from 'src/app/services/billing/billing-api.service';
import { BillingService } from 'src/app/services/billing/billing.service';
import { DiscountEligibilityService } from 'src/app/services/discount-eligibility/discount-eligibility.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps, SelfServeChatWithSalesGating } from 'src/app/services/growthbook/types';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { SelectCompany, UpdateCurrentUser } from 'src/app/store/auth/auth.actions';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';
import { AddonItem } from '../addons-page/addon.types';

export type BillingMode = 'automatic' | 'invoicing';
export type AddonTerm = 'monthly' | 'yearly' | 'quarterly' | 'semi_yearly';

type AddonFlag = Partial<AddonItem> & { linkName?: string };


export interface UpsellButtonsConfig {
  showLearnMoreBtn?: boolean;
  showLearnMoreStrokedBtn?: boolean;
  showContactUsBtn?: boolean;
}

@Component({
  selector: 'app-upsell-bottom-buttons',
  templateUrl: './upsell-bottom-buttons.component.html',
  styleUrls: ['./upsell-bottom-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsellBottomButtonsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Discount functionality
  enableDiscount = false;
  discountCodeValue = '';
  appliedDiscount: { discount: DiscountApiData, code: string } | null = null;
  validatingDiscount = false;
  discountValidationError: string | null = null;
  showingDiscountInput = false;
  availableDiscounts: DiscountApiData[] = [];

  // Shared string normalization utility for matching addon flag values
  // mode: 'words' keeps single spaces between alphanumerics; 'key' removes spaces entirely
  private normalizeString(value: unknown, mode: 'words' | 'key' = 'words'): string {
    let str = '';
    if (value == null) {
      str = '';
    } else if (typeof value === 'string') {
      str = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      str = String(value);
    } else if (typeof value === 'object') {
      try {
        // Avoid "[object Object]"; use JSON when possible
        str = JSON.stringify(value);
      } catch {
        str = '';
      }
    } else {
      str = '';
    }

    const base = str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    return mode === 'key' ? base.replace(/\s+/g, '') : base;
  }
  constructor(
    private store: Store,
    private readonly router: Router,
    private growthbook: GrowthBookService,
    private billingApiService: BillingApiService,
    private billingActionsService: BillingActionsService,
    private cdr: ChangeDetectorRef,
    private segment: SegmentService,
    private addonPricingService: AddonPricingService,
    private billingService: BillingService,
    private discountEligibilityService: DiscountEligibilityService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Initialize experiment properties
    this.buyNowButtonLabelExperiment = this.growthbook.getFeatureValue<ExperimentProps>('GT2-4286');
    // Default activation copy for all add-ons (i18n)
    this.dialogActivationText = this.translate.instant('billing.addonActivationDefault');
    // Prepare dialog pricing/unit text based on page context and GrowthBook
    this.prepareDialogChargeText();

    // Initialize discount functionality
    this.initializeDiscountFeature();
  }

  getBuyNowButtonLabel(): string {
    const isVariantB = this.buyNowButtonLabelExperiment?.variationid === 'B';

    return isVariantB
      ? this.translate.instant('billing.getAccessButton').toUpperCase()
      : this.translate.instant('billing.buyNowButton').toUpperCase();
  }

  // Identify the upsell page to compute defaults (e.g., tracking linkurl)
  @Input() pageName = '';

  // Optional overrides for contact button label and tracking
  @Input() contactLabel: string | null = null;
  @Input() contactTrackUrl: string | null = null;

  // Toggle Buy Now visibility (e.g., hide for Upgrade-only flows)
  @Input() enableBuyNow = true;

  // Upgrade CTA support (optional)
  @Input() showUpgradeBtn = false;
  @Input() canUpgrade = true;
  @Input() upgradeUrl: string | null = null;
  @Input() upgradeLabel: string | null = null;
  @Input() upgradeTrackUrl: string | null = null;

  // When provided, the component will handle the purchase flow internally
  @Input() addonIds: Partial<Record<AddonTerm, string>> | null = null;

  // Layout/visibility controls (internally computed to avoid duplication)
  get billingMode(): BillingMode {
    return this.store.selectSnapshot(NewBillingState.billingMode);
  }
  private readonly getSelfServeGating = () => this.growthbook.getFeatureValue<SelfServeChatWithSalesGating>('self-serve-chat-with-sales-gating', defaultSelfServeGating);

  get isSmallCompany(): boolean {
    try {
      const company = this.store.selectSnapshot(AuthSelectors.company);
      const userCount = company?.userCount ?? 0;
      const gating = this.getSelfServeGating();
      const min = Number(gating?.small?.min ?? 1);
      const rawMax = Number(gating?.small?.max ?? 50);
      const max = Number.isFinite(rawMax) ? rawMax : 50;
      return userCount >= min && userCount <= max;
    } catch {
      return false;
    }
  }
  get isLargeCompany(): boolean { // used by UAR to hide learn more
    try {
      const page = (this.pageName || '').toLowerCase();
      if (page === 'uar' || page === 'unusual-activity') {
        const company = this.store.selectSnapshot(AuthSelectors.company);
        const threshold = defaultSelfServeGating.small.max;
        return (company?.userCount ?? 0) > threshold;
      }
      return false;
    } catch {
      return false;
    }
  }
  get canShowBuyNow(): boolean {
    try {
      const company = this.store.selectSnapshot(AuthSelectors.company);
      const hasBillingAccess = company?.userSettings?.billingAccess === true;
      const isOwner = company?.role === 'owner';
      const isInvoicing = this.billingMode === 'invoicing';
      const isPaddle = company?.subscription?.provider === 'paddle';
      if (isInvoicing || !isPaddle) { return false; }
      return Boolean(isOwner || hasBillingAccess);
    } catch {
      return false;
    }
  }
  // External loading provided by parent (used for legacy paths like OVR)
  @Input() isLoading = false;
  private internalLoading = false;
  get effectiveLoading() { return this.isLoading || this.internalLoading; }

  // Experiment properties for A/B testing button labels
  private buyNowButtonLabelExperiment: ExperimentProps | null = null;

  // Content/links
  @Input() learnMoreUrl = '';

  // Upsell buttons visibility config coming from page logic
  @Input() upsellButtons: UpsellButtonsConfig | null = null;

  // Behavior tweaks to match page-specific UX
  @Input() contactRaisedWhenNoBuyNow = false; // UAR default: true in default layout

  // Tracking
  @Input() trackLocation = '';
  @Input() buyNowTrackUrl: string | null = null;

  // Optional explicit event names; fallback based on pageName
  @Input() purchaseEventName: string | null = null;
  @Input() purchaseErrorEventName: string | null = null;

  @Output() buyNow = new EventEmitter<MouseEvent>();
  @Output() contactSales = new EventEmitter<MouseEvent>();
  @Output() buyConfirmOpened = new EventEmitter<void>();
  @Output() buyConfirmClosed = new EventEmitter<void>();
  // Emitted when a purchase completes successfully (when handled internally)
  @Output() purchased = new EventEmitter<{ addonId: string, term: AddonTerm }>();
  /** When true, upgrade button click emits to upgradeClick and no navigation is performed (parent handles flow). */
  @Input() useUpgradeClickOutput = false;
  @Output() upgradeClick = new EventEmitter<MouseEvent>();

  // Buy confirmation dialog state and text controls
  showBuyConfirm = false;
  // Payment failed overlay state
  showPaymentFailed = false;
  paymentRetryLoading = false;
  @Input() chargeUnitText: string | null = 'per user per month'; // or 'per account'
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';

  // Dynamic dialog text built from GrowthBook for benchmark page
  dialogChargeText = 'You\'ll be charged add on price';
  dialogChargeUnitText: string | null = 'per user per month';
  dialogActivationText = 'This will be added to your next bill and activated within 24 hours.';
  // Store original price for discount calculation
  private originalPrice: string | null = null;
  // Store current term for confirmation text
  private currentTerm: AddonTerm | null = null;

  get showActivationLine(): boolean {
    const page = (this.pageName || '').toLowerCase();
    if (page.includes('benchmark')) return true;
    if (page.includes('uar') || page.includes('unusual') || page.includes('office-vs-remote') || page.includes('office vs remote')) return true;
    if (page.includes('meeting-insights') || page.includes('meeting insights')) return true;
    if (page.includes('video-screen-recording') || page.includes('video screen recording')) return true;
    if (page.includes('software-cost-insights') || page.includes('software cost insights')) return true;
    return false;
  }

  get resolvedBuyNowTrackUrl(): string {
    if (this.buyNowTrackUrl) { return this.buyNowTrackUrl; }
    switch ((this.pageName || '').toLowerCase()) {
      case 'uar':
      case 'unusual-activity':
        return 'uar_addon_buy_now';
      case 'benchmark':
      case 'benchmarks':
      case 'benchmarksai':
        return 'benchmark_addon_buy_now';
      case 'meeting-insights':
      case 'meeting insights':
        return 'meeting_insights_addon_buy_now';
      case 'video-screen-recording':
      case 'video screen recording':
        return 'video_screen_recording_addon_buy_now';
      case 'software-cost-insights':
      case 'software cost insights':
        return 'sci_addon_buy_now';
      default:
        return 'addon_buy_now';
    }
  }

  private prepareDialogChargeText() {
    try {
      const page = (this.pageName || '').toLowerCase();

      const feature = this.growthbook.getFeatureValue<{ flowId: string, addons: AddonFlag[] }>('addons-list', { flowId: '', addons: [] });
      const addons: Array<AddonFlag> = Array.isArray(feature?.addons) ? feature.addons : [];

      // Helpers for cleaner matching across pages
      const byLink = (val: string) => (a: AddonFlag) => {
        const ln = this.normalizeString(a?.linkname ?? a?.linkName);
        return ln === this.normalizeString(val);
      };
      const titleIncludes = (substr: string) => (a: AddonFlag) => {
        const t = (a?.title ?? '').toString().toLowerCase();
        return t.includes(substr.toLowerCase());
      };
      const findAddon = (...predicates: Array<(a: AddonFlag) => boolean>) => {
        for (const p of predicates) {
          const found = addons.find(p);
          if (found) { return found; }
        }
        return undefined;
      };
      const tryApplyFromFlag = (addon: AddonFlag | undefined) => {
        if (addon) { this.applyAddonTextFromFlag(addon as AddonItem); return true; }
        return false;
      };

      // Benchmarks page: keep existing behavior and activation text override
      if (page.includes('benchmark')) {
        const addon = findAddon(
          byLink('benchmarksai'),
          titleIncludes('benchmark'),
        );
        if (tryApplyFromFlag(addon)) { return; }
        return;
      }

      // Office vs Remote page: fetch OVR addon price and unit
      if (page.includes('office-vs-remote') || page.includes('office vs remote')) {
        const looksLikeOvr = (a: AddonFlag) => {
          const ln = this.normalizeString(a?.linkname ?? a?.linkName, 'key');
          const title = this.normalizeString(a?.title, 'key');
          return ln === 'ovr' || title === 'ovr' || ln.includes('officevsremote') || title.includes('officevsremote');
        };
        const addon = findAddon(
          looksLikeOvr,
          (a: Partial<AddonItem>) => {
            const t = (a?.title || '').toString().toLowerCase();
            return t.includes('office') && t.includes('remote');
          },
        );
        if (tryApplyFromFlag(addon)) { return; }
        return;
      }

      // UAR page: fetch UAR addon price and unit
      if (page.includes('uar') || page.includes('unusual')) {
        const addon = findAddon(
          byLink('uar'),
          titleIncludes('unusual activity'),
          titleIncludes('uar'),
        );
        if (tryApplyFromFlag(addon)) { return; }
        return;
      }


      // Meeting Insights page: fetch Meeting Insights addon price and unit
      if (page.includes('meeting-insights') || page.includes('meeting insights') || page.includes('meeting')) {
        const miAddon = addons.find((a: AddonFlag) => {
          const ln = this.normalizeString(a?.linkname ?? a?.linkName, 'key');
          const title = this.normalizeString(a?.title, 'key');
          const isMi = ln === 'meetinginsights' || title === 'meetinginsights';
          const looksLikeMi = (title.includes('meeting') && title.includes('insight')) || (ln.includes('meeting') && ln.includes('insight'));
          return isMi || looksLikeMi;
        });

        if (miAddon) {
          this.applyAddonTextFromFlag(miAddon as AddonItem);
        } else {
          // Fallback: any addon with title containing 'meeting' and 'insight'
          const fallback = addons.find((a: Partial<AddonItem>) => {
            const t = (a?.title || '').toString().toLowerCase();
            return t.includes('meeting') && t.includes('insight');
          });
          if (fallback) { this.applyAddonTextFromFlag(fallback as AddonItem); }
        }
        return;
      }

      if (page.includes('video-screen-recording') || page.includes('video screen recording') || page.includes('videoscreenrecording')) {
        const vsrAddon = findAddon(
          byLink('VideoScreenRecording'),
          byLink('video-screen-recording'),
          titleIncludes('video screen recording'),
        );
        if (tryApplyFromFlag(vsrAddon)) { return; }
        return;
      }

      // Software Cost Insights page: fetch Software Cost addon price and unit
      if (page.includes('software-cost-insights') || page.includes('software cost insights') || page.includes('software-cost')) {
        const looksLikeSci = (a: AddonFlag) => {
          const ln = this.normalizeString(a?.linkname ?? a?.linkName, 'key');
          const title = this.normalizeString(a?.title, 'key');
          return ln === 'softwarecostinsights' || title === 'softwarecostinsights' || ln.includes('softwarecost') || title.includes('softwarecost') || ln === 'sci' || title === 'sci';
        };
        const addon = findAddon(
          looksLikeSci,
          (a: Partial<AddonItem>) => {
            const t = (a?.title || '').toString().toLowerCase();
            return t.includes('software') && t.includes('cost');
          },
        );
        if (tryApplyFromFlag(addon)) { return; }
        return;
      }

      // Other pages: leave defaults
    } catch (error) {
      Sentry.captureMessage('Error preparing dialog charge text', { level: 'warning', extra: { error } });
    }
  }

  private applyAddonTextFromFlag(addon: AddonItem) {
    try {
      // Try to get dynamic pricing using AddonPricingService first
      if (this.addonIds && Object.values(this.addonIds).filter(Boolean).length > 0) {
        const currentPlan = this.getCurrentPlan();
        if (currentPlan) {

          // Convert partial addonIds to full AddonIds object
          const fullAddonIds: AddonIds = {
            monthly: this.addonIds.monthly || '',
            yearly: this.addonIds.yearly || '',
            quarterly: this.addonIds.quarterly || '',
            semi_yearly: this.addonIds.semi_yearly || '',
          };

          const pricingResult = this.addonPricingService.calculateAddonPricing(
            fullAddonIds,
            currentPlan,
            addon?.price || '$3.00',
            addon?.subtitle || 'per user/month',
          );

          // Store original price for discount calculation
          this.originalPrice = pricingResult.calculatedPrice;
          this.updateDialogChargeText();
          this.dialogChargeUnitText = pricingResult.calculatedSubtitle;
          this.cdr.detectChanges();
          return;
        }
      }

      // Fallback to static pricing from addon data
      const priceRaw = (addon?.price ?? '').toString().trim();
      const price = priceRaw || '$3.00';
      const subtitleRaw = (addon?.subtitle ?? '').toString();

      let unit = subtitleRaw.replace(/\s*\/\s*/g, '/').replace(/^(per|Per)\s+/, '').trim();
      if (!unit) { unit = 'user/month'; }
      unit = unit.toLowerCase();

      // Ensure unit has "per" prefix
      if (!unit.startsWith('per ')) {
        unit = `per ${unit}`;
      }

      // Store original price for discount calculation
      this.originalPrice = price;
      this.updateDialogChargeText();
      this.dialogChargeUnitText = unit;
      this.cdr.detectChanges();
    } catch (error) {
      Sentry.captureMessage('Error applying addon text from flag', { level: 'warning', extra: { error } });
    }
  }

  private resolveEventNames() {
    return {
      purchased: 'Addon Purchased',
      error: 'Addon Purchase Error',
    };
  }

  private getCurrentPlan(): ConcretePlan | null {
    try {
      const billingData = this.store.selectSnapshot(NewBillingState.billingData);
      if (!billingData) {
        return null;
      }

      const company = this.store.selectSnapshot(AuthSelectors.company);
      const userCount = billingData?.billingDetails?.quantity ?? company?.userCount ?? 1;
      const discountRemoval = this.growthbook.getFeatureValue('billing:discount_removal_companies', false);
      return new ConcretePlan(billingData, userCount, discountRemoval);
    } catch (error) {
      Sentry.captureMessage('Error creating current plan', { level: 'warning', extra: { error } });
      return null;
    }
  }

  private getTermFromContext(availableKeys: AddonTerm[]): AddonTerm | null {
    try {
      const currentPlan = this.getCurrentPlan();
      if (currentPlan) {
        return this.getTermFromPricingService(availableKeys, currentPlan);
      }
      return this.getTermFromFallbackLogic(availableKeys);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error getting term from context in upsell bottom buttons',
          component: 'UpsellBottomButtonsComponent',
        },
      });
      return null;
    }
  }

  private getTermFromPricingService(availableKeys: AddonTerm[], currentPlan: ConcretePlan): AddonTerm | null {
    const tempAddonIds: AddonIds = {
      monthly: availableKeys.includes('monthly') ? 'temp' : '',
      yearly: availableKeys.includes('yearly') ? 'temp' : '',
      quarterly: availableKeys.includes('quarterly') ? 'temp' : '',
      semi_yearly: availableKeys.includes('semi_yearly') ? 'temp' : '',
    };
    return this.addonPricingService.getTerms(tempAddonIds, currentPlan) as AddonTerm | null;
  }

  private getTermFromFallbackLogic(availableKeys: AddonTerm[]): AddonTerm | null {
    const termFromPricingPlan = this.getTermFromPricingPlan(availableKeys);
    if (termFromPricingPlan) return termFromPricingPlan;

    const termFromPaymentRecurrence = this.getTermFromPaymentRecurrence(availableKeys);
    if (termFromPaymentRecurrence) return termFromPaymentRecurrence;

    return this.getPreferredTerm(availableKeys);
  }

  private getTermFromPricingPlan(availableKeys: AddonTerm[]): AddonTerm | null {
    try {
      const company = this.store.selectSnapshot(AuthSelectors.company);
      const plan = (company?.pricingPlan || '').toLowerCase();
      if (!plan) return null;

      const termMappings = [
        { patterns: ['semi_year', 'semi-year', 'semiannual'], term: 'semi_yearly' as AddonTerm },
        { patterns: ['quarter'], term: 'quarterly' as AddonTerm },
        { patterns: ['annual', 'yearly'], term: 'yearly' as AddonTerm },
        { patterns: ['monthly'], term: 'monthly' as AddonTerm },
      ];

      for (const mapping of termMappings) {
        if (mapping.patterns.some(pattern => plan.includes(pattern) || plan.endsWith(`_${pattern}`))) {
          if (availableKeys.includes(mapping.term)) return mapping.term;
        }
      }
      return null;
    } catch (error) {
      Sentry.captureMessage('Error deriving recurrence from pricingPlan for addon purchase', { level: 'warning', extra: { error } });
      return null;
    }
  }

  private getTermFromPaymentRecurrence(availableKeys: AddonTerm[]): AddonTerm | null {
    try {
      const currentPlan = this.getCurrentPlan();
      const recurrenceRaw = currentPlan?.paymentModel?.paymentRecurrence as string | undefined;
      const recurrence = (recurrenceRaw || '').toLowerCase();
      const mapRecurrenceKey: Record<string, AddonTerm> = {
        year: 'yearly', yearly: 'yearly', annual: 'yearly', annually: 'yearly',
        month: 'monthly', monthly: 'monthly',
        quarter: 'quarterly', quarterly: 'quarterly',
        'semi-year': 'semi_yearly', semi_year: 'semi_yearly', 'semi-annual': 'semi_yearly', semiannual: 'semi_yearly',
      };

      if (recurrence && recurrence in mapRecurrenceKey) {
        const term = mapRecurrenceKey[recurrence];
        if (availableKeys.includes(term)) return term;
      }
      return null;
    } catch (error) {
      Sentry.captureMessage('Error getting payment recurrence from current plan', { level: 'warning', extra: { error } });
      return null;
    }
  }

  private getPreferredTerm(availableKeys: AddonTerm[]): AddonTerm | null {
    const preferredOrder: AddonTerm[] = ['monthly', 'yearly', 'quarterly', 'semi_yearly'];
    return preferredOrder.find(k => availableKeys.includes(k)) || availableKeys[0] || null;
  }

  openBuyConfirm(event: MouseEvent) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    // Reset any previous failure overlay state
    this.showPaymentFailed = false;
    this.paymentRetryLoading = false;
    // Determine and store the current term for confirmation text
    if (this.addonIds) {
      const availableKeys = (Object.keys(this.addonIds) as AddonTerm[]).filter(k => !!this.addonIds[k]);
      this.currentTerm = this.getTermFromContext(availableKeys);
    } else {
      this.currentTerm = null;
    }
    // Prepare dialog text for Benchmark page based on GrowthBook flags
    this.prepareDialogChargeText();
    // Open confirmation dialog; actual purchase happens after confirm
    this.showBuyConfirm = true;
    this.buyConfirmOpened.emit();
    this.cdr.detectChanges();
  }

  private doPurchase(event?: MouseEvent) {
    // If no addonIds provided, keep legacy behavior and let parent handle it
    if (!this.addonIds || Object.values(this.addonIds).filter(Boolean).length === 0) {
      this.buyNow.emit(event);
      return;
    }

    if (event) { event.preventDefault(); event.stopPropagation(); }
    this.internalLoading = true;

    // Determine available keys from provided addonIds
    const availableKeys = (Object.keys(this.addonIds) as AddonTerm[]).filter(k => !!this.addonIds[k]);
    if (availableKeys.length === 0) {
      Sentry.captureMessage('Addon purchase aborted: no available addonIds provided', { level: 'warning', extra: { addonIds: this.addonIds } });
      this.internalLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const term = this.getTermFromContext(availableKeys);
    if (!term) {
      Sentry.captureMessage('Addon purchase aborted: unable to determine term', { level: 'warning', extra: { addonIds: this.addonIds } });
      this.internalLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const addonId = this.addonIds[term];
    if (!addonId) {
      Sentry.captureMessage('AddonId missing for selected term', { level: 'warning', extra: { term, addonIds: this.addonIds } });
      this.internalLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const events = this.resolveEventNames();
    const discountId = this.appliedDiscount?.discount?.discountId;

    this.billingApiService.createAddOnLineItem(addonId, discountId).pipe(
    ).subscribe({
      next: (response) => {
        this.internalLoading = false;
        this.paymentRetryLoading = false;
        // Hide payment failed overlay if it was shown previously
        this.showPaymentFailed = false;
        if (response) {
          // Optimistically update company subscription addOns
          const currentCompany = this.store.selectSnapshot(AuthSelectors.company);
          if (currentCompany?.subscription?.addOns && !currentCompany.subscription.addOns.includes(addonId)) {
            const updatedCompany = {
              ...currentCompany,
              subscription: {
                ...currentCompany.subscription,
                addOns: [...currentCompany.subscription.addOns, addonId],
              },
            } as any;
            this.store.dispatch(new SelectCompany(updatedCompany));
          } else {
            this.store.dispatch(new UpdateCurrentUser(true));
          }

          // Get addon data for tracking
          let addonData: any = {};
          try {
            const currentPlan = this.getCurrentPlan();
            if (currentPlan) {

              // Convert partial addonIds to full AddonIds object
              const fullAddonIds: AddonIds = {
                monthly: this.addonIds.monthly || '',
                yearly: this.addonIds.yearly || '',
                quarterly: this.addonIds.quarterly || '',
                semi_yearly: this.addonIds.semi_yearly || '',
              };

              // Get the addon plan data
              const addonPlan = this.addonPricingService.getAddonPlanForCurrentRecurrence(fullAddonIds, currentPlan);
              if (addonPlan) {
                addonData = {
                  title: addonPlan.name,
                  price: this.addonPricingService.formatAddonPrice(addonPlan.unitPrice),
                  subtitle: this.addonPricingService.formatAddonSubtitle(addonPlan, currentPlan.paymentModel?.paymentRecurrence),
                };
              }
            }
          } catch (error) {
            Sentry.captureMessage('Error getting addon data for tracking', { level: 'warning', extra: { error } });
          }

          // Track purchase event
          this.segment.trackInAppEvents(events.purchased, {
            location: this.trackLocation || 'Upsell Page',
            addonId,
            term,
            addonTitle: addonData.title,
            price: addonData.price,
            billingPeriod: addonData.subtitle,
            discountId: discountId || null,
            discountApplied: !!discountId,
          });

          // Close the confirmation dialog on success
          this.showBuyConfirm = false;
          this.buyConfirmClosed.emit();

          // Emit purchased event so parents can react immediately (e.g., refresh UI state)
          try { this.purchased.emit({ addonId, term }); } catch {}

          // Refresh billing details with polling until the new add-on line item appears (with createdAt)
          try {
            const hasNewAddonLineItem = (d: BillingDetailsApiData) => !!d?.lineItems?.some(li => li.planId === addonId && !!li.createdAt);
            this.billingService
              .pollBillingDetailsUntilConditionMet(hasNewAddonLineItem, { pollingRetryCount: 5, pollingBaseDelayMs: 500 })
              .subscribe({
                // Success path is enough; BillingService.getBillingDetails already dispatches UpdateBillingDetails in its tap
                // Poll method internally calls getBillingDetails('no-cache') on each attempt
                next: () => {},
                error: () => { /* non-blocking refresh */ },
              });
          } catch { /* noop */ }
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        Sentry.captureException(error, { extra: { message: 'Failed to create addon line item', addonId, term } });
        this.segment.trackInAppEvents(events.error, {
          location: this.trackLocation || 'Upsell Page',
          addonId,
          term,
          error: error?.message || 'Unknown error',
        });

        // Close confirmation and show Payment Failed overlay
        this.showBuyConfirm = false;
        this.buyConfirmClosed.emit();
        this.showPaymentFailed = true;

        this.internalLoading = false;
        this.paymentRetryLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  confirmBuyNow() {
    // Keep dialog open and show loading while processing when we handle purchase here
    const hasAddonIds = this.addonIds && Object.values(this.addonIds).filter(Boolean).length > 0;
    if (hasAddonIds) {
      this.doPurchase();
      // doPurchase will set internalLoading=true and will close the dialog on success
      return;
    }
    // Legacy: no addonIds provided; delegate to parent and close dialog immediately
    this.showBuyConfirm = false;
    this.buyConfirmClosed.emit();
    this.doPurchase();
  }

  cancelBuyConfirm() {
    // Clear any inputted coupon when cancel is clicked
    if (this.enableDiscount && (this.discountCodeValue || this.appliedDiscount || this.showingDiscountInput)) {
      this.clearDiscount();
    }

    this.showBuyConfirm = false;
    this.buyConfirmClosed.emit();
    this.cdr.detectChanges();
  }

  // Payment Failed overlay handlers
  onPaymentRetry() {
    // Close the failed payment dialog and reopen the buy confirmation dialog
    this.showPaymentFailed = false;
    this.paymentRetryLoading = false;
    // Re-prepare dialog text in case anything changed and open confirm
    this.prepareDialogChargeText();
    this.showBuyConfirm = true;
    this.buyConfirmOpened.emit();
    this.cdr.detectChanges();
  }

  onPaymentDismiss() {
    this.showPaymentFailed = false;
    this.paymentRetryLoading = false;
    this.cdr.detectChanges();
  }

  onContactSales(event: MouseEvent) {
    this.contactSales.emit(event);
  }

  onUpgrade(event: MouseEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.upgradeUrl) { return; }

    if (this.useUpgradeClickOutput) {
      this.upgradeClick.emit(event);
      return;
    }

    try {
      // Parse the URL to extract route and query parameters
      const url = new URL(this.upgradeUrl, globalThis.location.origin);
      const routePath = url.pathname;
      const queryParams: Record<string, string> = {};

      // Extract query parameters
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      // Navigate using Angular Router
      void this.router.navigate([routePath], { queryParams });
    } catch {
      // Fallback: if URL parsing fails, try to navigate directly
      // Handle relative URLs (e.g., /new-billing?choosePlan=yes)
      if (this.upgradeUrl.startsWith('/')) {
        const [path, queryString] = this.upgradeUrl.split('?');
        const queryParams: Record<string, string> = {};

        if (queryString) {
          queryString.split('&').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value) {
              queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
            }
          });
        }

        void this.router.navigate([path], { queryParams });
      } else {
        // If it's not a relative URL, fall back to href behavior
        globalThis.location.href = this.upgradeUrl;
      }
    }
  }

  onPaymentUpdatePaymentMethod() {
    try {
      // Close the failed payment dialog first
      this.showPaymentFailed = false;
      this.paymentRetryLoading = false;

      // Build a ConcretePlan from current billing data to satisfy checkout dialog requirements
      const plan = this.getCurrentPlan();
      if (!plan) {
        // If we cannot get billing data, show a helpful message and abort
        this.billingActionsService.openSnackbar({ message: 'Unable to open payment method update right now. Please try again.', duration: 5000 });
        this.cdr.detectChanges();
        return;
      }

      this.billingActionsService.openCheckoutV2Dialog({
        currentPlan: plan,
        isUpdatingCard: true,
        trackingCtx: { location: this.trackLocation || 'Upsell Payment Failed' },
      });

      this.cdr.detectChanges();
    } catch (error) {
      Sentry.captureMessage('Error opening checkout dialog in edit mode from payment failed', { level: 'error', extra: { error } });
    }
  }

  get learnMoreLabel(): string {
    let label = '';
    const page = (this.pageName || '').toLowerCase();

    if (page === 'uar' || page === 'unusual-activity') {
      label= this.translate.instant('unusualActivity.upsell.learnMore');
    } else {
      label = this.translate.instant('common.learnMore');
    }
    return label;
  }

  get contactButtonText(): string {
    if (this.contactLabel) { return this.contactLabel; }
    return this.translate.instant('common.contactSales');
  }

  get upgradeButtonText(): string {
    if (this.upgradeLabel) { return this.upgradeLabel; }
    return this.translate.instant('common.upgrade');
  }

  get resolvedUpgradeTrackUrl(): string {
    if (this.upgradeTrackUrl) { return this.upgradeTrackUrl; }
    return 'addon_upgrade';
  }

  // Discount functionality methods
  private initializeDiscountFeature(): void {
    this.growthbook.getFeature<boolean>('enable-discount-on-addons', false)
      .pipe(
        switchMap(enabled => {
          if (!enabled) {
            return of({ enabled: false, loadDiscounts: false });
          }

          const eligibilityFlag = this.growthbook.getFeatureValue<boolean>('add-coupons-to-companies', false);
          if (!eligibilityFlag) {
            return of({ enabled: true, loadDiscounts: true });
          }

          const companyId = this.store.selectSnapshot(AuthSelectors.company)?.id;
          if (!companyId) {
            Sentry.captureMessage('Discount eligibility check skipped due to missing companyId', {
              level: 'warning',
              tags: { feature: 'add-coupons-to-companies' },
            });
            return of({ enabled: false, loadDiscounts: false });
          }

          return this.discountEligibilityService.isDiscountAllowed(companyId).pipe(
            map(isAllowed => ({ enabled: isAllowed, loadDiscounts: isAllowed })),
            catchError(error => {
              Sentry.captureException(error, {
                level: 'error',
                tags: { feature: 'add-coupons-to-companies' },
                extra: { companyId },
              });
              return of({ enabled: false, loadDiscounts: false });
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ enabled, loadDiscounts }) => {
        this.enableDiscount = enabled;
        if (enabled && loadDiscounts) {
          this.loadAvailableDiscounts();
        } else {
          this.availableDiscounts = [];
        }
        this.cdr.detectChanges();
      });
  }

  private loadAvailableDiscounts(): void {
    try {
      const addonCoupons = this.growthbook.getFeatureValue<DiscountApiData[]>('addon-coupons', []);
      if (Array.isArray(addonCoupons)) {
        this.availableDiscounts = addonCoupons;
      } else {
        console.warn('GrowthBook addon-coupons feature flag returned invalid data format');
        this.availableDiscounts = [];
      }
    } catch (error) {
      console.error('Failed to load discounts from GrowthBook:', error);
      this.availableDiscounts = [];
    }
  }

  private validateDiscountCode(discountCode: string, addonIds: AddonIds): DiscountApiData | null {
    if (!discountCode.trim() || !this.availableDiscounts.length) {
      return null;
    }

    const normalizedCode = discountCode.trim().toUpperCase();
    const addonIdValues = Object.values(addonIds);
    return this.availableDiscounts.find(discount => {
      const discountCodeUpper = discount.discountId.toUpperCase();
      const isCodeMatch = discountCodeUpper === normalizedCode;

      // Check if discount is available for any of the addon plans
      const isPlanAvailable = !discount.availablePlans ||
        discount.availablePlans.some(planId => addonIdValues.includes(planId));

      // Check if discount is compatible with current subscription recurrence
      const isRecurrenceCompatible = this.isDiscountCompatibleWithCurrentRecurrence(discount, addonIds);

      return isCodeMatch && isPlanAvailable && isRecurrenceCompatible;
    }) || null;
  }

  private isDiscountCompatibleWithCurrentRecurrence(discount: DiscountApiData, addonIds: AddonIds): boolean {
    if (!this.getCurrentPlan() || !discount.availablePlans) {
      return true; // If no current plan or no availablePlans, allow the discount
    }

    const currentTerm = this.addonPricingService.getTerms(addonIds, this.getCurrentPlan());
    if (!currentTerm) {
      return true; // If we can't determine the current term, allow the discount
    }

    const currentTermAddonId = addonIds[currentTerm];
    if (!currentTermAddonId) {
      return true; // If no addon ID for current term, allow the discount
    }

    // Check if the discount's availablePlans includes the current term's addon ID
    const isCompatible = discount.availablePlans.includes(currentTermAddonId as any);
    return isCompatible;
  }

  applyDiscountCode(): void {
    if (!this.discountCodeValue.trim() || !this.addonIds) {
      return;
    }

    // Track CTA click for apply discount button
    this.segment.trackInAppEvents('Link Click', {
      linklocation: this.trackLocation || 'Upsell Page - Purchase Confirmation',
      linkname: 'Apply Discount Code Button',
      linkurl: '',
      linktype: 'button',
      addonTitle: this.getAddonTitle(),
      discountCode: this.discountCodeValue,
    });

    this.validatingDiscount = true;
    this.discountValidationError = null;

    const validatedDiscount = this.validateDiscountCode(this.discountCodeValue, this.addonIds as AddonIds);

    if (validatedDiscount) {
      this.appliedDiscount = {
        discount: validatedDiscount,
        code: this.discountCodeValue,
      };

      // Update dialog charge text with discount
      this.updateDialogChargeText();

      // Track successful discount code application
      this.segment.trackInAppEvents('Discount Code Applied', {
        location: this.trackLocation || 'Upsell Page - Purchase Confirmation',
        addonId: Object.values(this.addonIds as AddonIds)[0],
        discountCode: this.discountCodeValue,
        discountId: validatedDiscount.discountId,
        discountType: validatedDiscount.type,
        discountValue: validatedDiscount.value,
      });

      this.discountCodeValue = '';
      this.showingDiscountInput = false;
    } else {
      // Track failed discount code application
      this.segment.trackInAppEvents('Discount Code Invalid', {
        location: this.trackLocation || 'Upsell Page - Purchase Confirmation',
        addonId: Object.values(this.addonIds as AddonIds)[0],
        discountCode: this.discountCodeValue,
      });

      this.discountValidationError = 'Invalid discount code. Please check and try again.';
    }

    this.validatingDiscount = false;
    this.cdr.detectChanges();
  }

  clearDiscount(): void {
    // Track CTA click for delete discount code link
    if (this.appliedDiscount && this.addonIds) {
      this.segment.trackInAppEvents('Link Click', {
        linklocation: this.trackLocation || 'Upsell Page - Purchase Confirmation',
        linkname: 'Delete Discount Code Link',
        linkurl: '',
        linktype: 'link',
        addonTitle: this.getAddonTitle(),
        discountCode: this.appliedDiscount.code,
      });
    }

    this.appliedDiscount = null;
    this.discountCodeValue = '';
    this.discountValidationError = null;
    this.showingDiscountInput = false;
    // Update dialog charge text to remove discount
    this.updateDialogChargeText();
    this.cdr.detectChanges();
  }

  /**
   * Gets the addon title for the current addonIds and plan
   * @returns string | null - The addon title or null if unavailable
   */
  private getAddonTitle(): string | null {
    if (!this.addonIds) {
      return null;
    }

    try {
      const currentPlan = this.getCurrentPlan();
      if (!currentPlan) {
        return null;
      }

      const fullAddonIds: AddonIds = {
        monthly: this.addonIds.monthly || '',
        yearly: this.addonIds.yearly || '',
        quarterly: this.addonIds.quarterly || '',
        semi_yearly: this.addonIds.semi_yearly || '',
      };

      const addonPlan = this.addonPricingService.getAddonPlanForCurrentRecurrence(fullAddonIds, currentPlan);
      return addonPlan?.name || null;
    } catch (error) {
      Sentry.captureMessage('Error getting addon title for tracking', { level: 'warning', extra: { error } });
      return null;
    }
  }

  showDiscountInput(): void {
    if (this.addonIds) {
      this.segment.trackInAppEvents('Link Click', {
        linklocation: 'Upsell Page - Purchase Confirmation',
        linkname: 'Discount Code Link',
        linkurl: '',
        linktype: 'link',
        addonTitle: this.getAddonTitle(),
      });
    }

    this.showingDiscountInput = true;
    this.discountCodeValue = '';
    this.discountValidationError = null;
    this.cdr.detectChanges();
  }

  /**
   * Clears the discount validation error when user modifies the coupon code
   */
  clearDiscountValidationError(): void {
    this.discountValidationError = null;
    this.cdr.detectChanges();
  }

  /**
   * Handles discount code input changes and clears validation error
   * @param value - The new discount code value
   */
  onDiscountCodeChange(value: string): void {
    this.discountCodeValue = value;
    this.clearDiscountValidationError();
  }

  getAppliedDiscountText(): string {
    if (!this.appliedDiscount) {
      return '';
    }

    const { discount } = this.appliedDiscount;
    if (discount.type === 'percentage') {
      return `Discount applied: ${discount.value}% off`;
    } else if (discount.type === 'amount') {
      return `Discount applied: $${discount.value} off`;
    } else if (discount.type === 'amount_per_seat') {
      return `Discount applied: $${discount.value} per seat off`;
    }
    return 'Discount applied';
  }

  onDiscountCodeKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyDiscountCode();
    } else if (event.key === 'Escape') {
      this.clearDiscount();
    }
  }

  /**
   * Parses a price string with locale-aware decimal and thousand separators
   * @param priceString - The price string to parse
   * @returns number - The parsed numeric value
   */
  private parsePriceWithLocale(priceString: string): number {
    try {
      // Get user's locale
      const locale = Intl.NumberFormat().resolvedOptions().locale;

      // Detect decimal and thousand separators by formatting known numbers
      const decimalSeparator = new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value || '.';
      const thousandSeparator = new Intl.NumberFormat(locale).formatToParts(1000).find(part => part.type === 'group')?.value || ',';

      // Remove currency symbols and whitespace
      const currencyRegex = /[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿]/g;
      // eslint-disable-next-line
      let cleanPrice = priceString.replace(currencyRegex, '').trim();

      // Remove thousand separators
      if (thousandSeparator !== decimalSeparator) {
        // eslint-disable-next-line
        cleanPrice = cleanPrice.replace(new RegExp(`\\${thousandSeparator}`, 'g'), '');
      }

      // Replace locale decimal separator with standard dot
      if (decimalSeparator !== '.') {
        // eslint-disable-next-line
        cleanPrice = cleanPrice.replace(new RegExp(`\\${decimalSeparator}`, 'g'), '.');
      }

      // Parse the normalized string
      const numericValue = Number.parseFloat(cleanPrice);

      // Fallback to NaN handling
      return Number.isNaN(numericValue) ? 0 : numericValue;
    } catch {
      // Fallback to simple parsing if locale detection fails
      // eslint-disable-next-line
      const cleanPrice = priceString.replace(/[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿,]/g, '').trim();
      const numericValue = Number.parseFloat(cleanPrice);
      return Number.isNaN(numericValue) ? 0 : numericValue;
    }
  }

  /**
   * Gets the original price for display
   * @returns string - The original price
   */
  getOriginalPrice(): string {
    if (!this.originalPrice) {
      return '';
    }
    // Extract just the price part (e.g., "$3.00" from "You'll be charged $3.00" or just "$3.00")
    return this.extractPriceFromOriginal(this.originalPrice);
  }

  /**
   * Gets the discounted price for display
   * @returns string - The discounted price
   */
  getDiscountedPrice(): string {
    if (!this.appliedDiscount || !this.originalPrice) {
      return this.getOriginalPrice();
    }

    // Extract price part first (in case originalPrice contains full text)
    const priceText = this.extractPriceFromOriginal(this.originalPrice);
    const originalPrice = this.parsePriceWithLocale(priceText);
    const { discount } = this.appliedDiscount;

    let discountedPrice: number;
    if (discount.type === 'percentage') {
      discountedPrice = originalPrice * (1 - discount.value / 100);
    } else if (discount.type === 'amount') {
      discountedPrice = Math.max(0, originalPrice - discount.value);
    } else if (discount.type === 'amount_per_seat') {
      // For per-seat discounts, we'd need user count, but for now just apply the discount
      discountedPrice = Math.max(0, originalPrice - discount.value);
    } else {
      return this.getOriginalPrice();
    }

    // Format the discounted price with the same currency symbol as original
    const currencyRegex = /[₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿]/;
    const currencyMatch = currencyRegex.exec(priceText);
    const currencySymbol = currencyMatch ? currencyMatch[0] : '$';

    return `${currencySymbol}${discountedPrice.toFixed(2)}`;
  }

  /**
   * Updates the dialog charge text based on whether a discount is applied
   */
  private updateDialogChargeText(): void {
    if (!this.originalPrice) {
      return;
    }

    // Extract just the price part (e.g., "$3.00" from "You'll be charged $3.00")
    const priceText = this.extractPriceFromOriginal(this.originalPrice);

    if (this.appliedDiscount) {
      // When discount is applied, the template will show original and discounted prices
      // We'll pass both separately through the component
      this.dialogChargeText = `You'll be charged ${priceText}`;
    } else {
      // No discount, show original price
      this.dialogChargeText = `You'll be charged ${priceText}`;
    }
  }

  /**
   * Extracts the price part from the original price string
   * @param originalPrice - The original price string
   * @returns string - The extracted price
   */
  private extractPriceFromOriginal(originalPrice: string): string {
    // Extract price from "You'll be charged $X.XX" format or just "$X.XX"
    const priceMatch = originalPrice.match(/([₹$€£¥₽₩₪₫₨₦₡₱₴₸₼₾₿][\d,]+\.?\d*)/);
    if (priceMatch) {
      return priceMatch[1];
    }
    return originalPrice;
  }

  /**
   * Checks if a discount is applied
   * @returns boolean - True if discount is applied
   */
  isDiscountApplied(): boolean {
    return !!this.appliedDiscount;
  }

  /**
   * Gets the confirmation text prefix (before the price)
   * @returns string - The translated prefix text
   */
  getConfirmationTextPrefix(): string {
    return this.translate.instant('billing.addonConfirmationTextPrefix');
  }

  /**
   * Gets the confirmation text suffix (after the price)
   * @returns string - The translated suffix text based on billing period
   */
  getConfirmationTextSuffix(): string {
    // If we have a current term, use the appropriate translation based on billing period
    if (this.currentTerm) {
      const termToTranslationKey: Record<AddonTerm, string> = {
        monthly: 'common.perUserPerMonth',
        yearly: 'common.perUserPerYear',
        quarterly: 'common.perUserPerQuarter',
        semi_yearly: 'common.perUserPerSemiYear',
      };
      const translationKey = termToTranslationKey[this.currentTerm];
      if (translationKey) {
        // Add leading space and period to match the original format
        const translatedText = this.translate.instant(translationKey);
        return ' ' + translatedText + '.';
      }
    }
    // Fallback to default monthly text
    return this.translate.instant('billing.addonConfirmationTextSuffix');
  }

  /**
   * Gets the formatted price for display in confirmation message
   * @returns string - The formatted price (same as original price for display)
   */
  getFormattedPriceForConfirmation(): string {
    return this.getOriginalPrice();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
